import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import {
  CONSENT_STORAGE_KEY,
  OPTIONAL_CONSENT_DEFAULTS,
  clearOptionalTrackingStorage,
  createConsentRecord,
  parseConsentRecord,
  readConsentRecord,
  writeConsentRecord,
  type ConsentPreferences,
  type ConsentRecord,
} from './consent.storage'

interface ConsentContextValue {
  consent: ConsentRecord | null
  preferencesOpen: boolean
  openPreferences: () => void
  closePreferences: () => void
  acceptAll: () => void
  rejectOptional: () => void
  savePreferences: (preferences: Pick<ConsentPreferences, 'analytics' | 'media'>) => void
  allowMedia: () => void
}

const ConsentContext = createContext<ConsentContextValue | null>(null)

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<ConsentRecord | null>(() => readConsentRecord())
  const [preferencesOpen, setPreferencesOpen] = useState(false)

  const persistPreferences = useCallback((preferences: Pick<ConsentPreferences, 'analytics' | 'media'>) => {
    const next = createConsentRecord(preferences)
    const mustRestartTelemetry = consent?.analytics === true && next.analytics === false

    writeConsentRecord(next)
    setConsent(next)
    setPreferencesOpen(false)

    if (!next.analytics) {
      clearOptionalTrackingStorage()
    }

    if (mustRestartTelemetry) {
      window.location.reload()
    }
  }, [consent?.analytics])

  const acceptAll = useCallback(() => {
    persistPreferences({ analytics: true, media: true })
  }, [persistPreferences])

  const rejectOptional = useCallback(() => {
    persistPreferences(OPTIONAL_CONSENT_DEFAULTS)
  }, [persistPreferences])

  const allowMedia = useCallback(() => {
    persistPreferences({
      analytics: consent?.analytics ?? false,
      media: true,
    })
  }, [consent?.analytics, persistPreferences])

  useEffect(() => {
    if (!consent?.analytics) return

    void Promise.all([
      import('@/lib/firebase/analytics').then(({ initializeAnalytics }) => initializeAnalytics()),
      import('@/lib/firebase/performance').then(({ initializePerformance }) => initializePerformance()),
    ])
  }, [consent?.analytics])

  useEffect(() => {
    function syncConsent(event: StorageEvent) {
      if (event.key !== CONSENT_STORAGE_KEY) return
      const next = parseConsentRecord(event.newValue)
      if (consent?.analytics && !next?.analytics) {
        clearOptionalTrackingStorage()
        window.location.reload()
        return
      }
      setConsent(next)
    }

    window.addEventListener('storage', syncConsent)
    return () => window.removeEventListener('storage', syncConsent)
  }, [consent?.analytics])

  const value = useMemo<ConsentContextValue>(() => ({
    consent,
    preferencesOpen,
    openPreferences: () => setPreferencesOpen(true),
    closePreferences: () => setPreferencesOpen(false),
    acceptAll,
    rejectOptional,
    savePreferences: persistPreferences,
    allowMedia,
  }), [acceptAll, allowMedia, consent, persistPreferences, preferencesOpen, rejectOptional])

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
}

export function useConsent(): ConsentContextValue {
  const context = useContext(ConsentContext)
  if (!context) throw new Error('useConsent must be used within ConsentProvider')
  return context
}
